import { type ReactNode, useEffect } from 'react';
import { useAuth } from './hooks.js';

interface RequireAuthProps {
  children: ReactNode;
  /** Rendered while auth state is being determined */
  fallback?: ReactNode;
  /**
   * Called when the user is not authenticated and auth has finished loading.
   * Defaults to triggering the Auth0 login redirect.
   * Override to redirect to an in-app login route instead.
   */
  onUnauthenticated?: () => void;
}

/**
 * Route guard — calls onUnauthenticated (defaults to Auth0 loginWithRedirect)
 * when the user is not authenticated.
 */
export function RequireAuth({ children, fallback = null, onUnauthenticated }: RequireAuthProps) {
  const { isAuthenticated, isLoading, login } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      onUnauthenticated ? onUnauthenticated() : login();
    }
  }, [isAuthenticated, isLoading, login, onUnauthenticated]);

  if (isLoading || !isAuthenticated) return <>{fallback}</>;

  return <>{children}</>;
}
